import type { ReactNode } from 'react'

import { useIntl } from 'react-intl'

import {
	InsuranceCovered,
	InsuranceOrigin,
	InsuranceType as InsuranceTypEnum,
	type Insurance as InsuranceType,
} from '@subaio/api-types/service'
import { FormattedMessage } from '@subaio/components'
import { cases, isNonNullable } from '@subaio/utils'

import type { OnLogEvent } from 'lib/container'

import { ButtonSolid, ButtonTransparent } from 'components/Buttons'
import { FormattedNumber } from 'components/FormattedNumber'
import { IconCheckPlain, IconInfo, IconLink, IconWarning, IconX } from 'components/Icons'
import { ButtonLabel } from 'components/Layout'
import { PolicyItem, PolicyList } from 'components/Lists'
import { Logo } from 'components/Logo'
import { useModal } from 'components/Modal'
import { ProductLogo } from 'components/ProductLogo'

import { Amount } from './Amount'
import { ButtonGroup } from './ButtonGroup'
import { ButtonIcon } from './ButtonIcon'
import { Container } from './Container'
import { Modal } from './Modal'
import { PolicyScore } from './PolicyScore'
import { Subtitle } from './Subtitle'
import { Title } from './Title'

type Props = {
	insurance: InsuranceType
	subtitle?: ReactNode
	areTermsAndConditionsExternal?: boolean

	onShowSwitching: () => void
	onShowTermsAndConditions?: () => void
	onLogScopedEvent: OnLogEvent
}

export const Insurance = ({
	insurance,
	subtitle,
	areTermsAndConditionsExternal,
	onShowSwitching,
	onShowTermsAndConditions,
	onLogScopedEvent,
}: Props) => {
	const { title, productTitle, imageUrl, origin, amountMonthly, coverageSummary, currency, policyScore, type } =
		insurance
	const { formatMessage } = useIntl()
	const { showCard } = useModal()

	return (
		<>
			<Container>
				{cases(origin, {
					[InsuranceOrigin.Existing]: (
						<ProductLogo
							aria-label={formatMessage(
								{ id: 'insurance.logo', defaultMessage: 'Logo {productTitle}' },
								{ productTitle },
							)}
							role='img'
							title={productTitle}
							imageUrl={imageUrl}
							size={48}
						/>
					),
					[InsuranceOrigin.Offer]: (
						<Logo
							title={formatMessage({ id: 'insurance.logo', defaultMessage: 'Logo {productTitle}' }, { productTitle })}
						/>
					),
				})}
				<Title>{title}</Title>

				<hr aria-hidden />

				<b>
					<FormattedMessage id='insurance.amountMonthly' defaultMessage='Premium per month' />
				</b>
				<Amount>
					<FormattedNumber value={amountMonthly} currency={currency} showCurrency />
				</Amount>
				{subtitle && <Subtitle>{subtitle}</Subtitle>}
				{isNonNullable(policyScore) && (
					<PolicyScore>
						{cases(type, {
							[InsuranceTypEnum.Travel]: (
								<FormattedMessage
									id='insurance.policyScore'
									defaultMessage='Policy score {policyScore}'
									values={{ policyScore }}
								/>
							),
							[InsuranceTypEnum.Car]: (
								<FormattedMessage
									id='insurance.coverageScore'
									defaultMessage='Coverage score {policyScore}'
									values={{ policyScore }}
								/>
							),
						})}
						<ButtonIcon
							aria-label={cases(type, {
								[InsuranceTypEnum.Travel]: formatMessage({
									id: 'insurance.buttonPolicyScore',
									defaultMessage: 'Information on policy score',
								}),
								[InsuranceTypEnum.Car]: formatMessage({
									id: 'insurance.buttonCoverageScore',
									defaultMessage: 'Information on coverage score',
								}),
							})}
							onClick={() => {
								onLogScopedEvent('COMPARISON_COVERAGE_SCORE')
								showCard((props) => <Modal {...props} type={type} />)
							}}
						>
							<IconInfo />
						</ButtonIcon>
					</PolicyScore>
				)}

				<hr aria-hidden />

				<p>
					<b>
						<FormattedMessage id='insurance.conditionsTitle' defaultMessage='Main conditions' />
					</b>
				</p>

				<PolicyList>
					{coverageSummary.map(({ title, covered }, index) => (
						<PolicyItem key={index} isCovered={[InsuranceCovered.Yes, InsuranceCovered.Warning].includes(covered)}>
							{cases(covered, {
								[InsuranceCovered.Yes]: <IconCheckPlain />,
								[InsuranceCovered.No]: <IconX />,
								[InsuranceCovered.Warning]: <IconWarning />,
							})}
							<span
								role='text'
								aria-label={cases(covered, {
									[InsuranceCovered.Yes]: `${formatMessage({ id: 'insurance.policyItemCovered', defaultMessage: 'Included' })}. ${title}`,
									[InsuranceCovered.No]: `${formatMessage({ id: 'insurance.policyItemNotCovered', defaultMessage: 'Not included' })}. ${title}`,
									[InsuranceCovered.Warning]: `${formatMessage({ id: 'insurance.policyItemWarning', defaultMessage: 'Beware' })}. ${title}`,
								})}
							>
								{title}
							</span>
						</PolicyItem>
					))}
				</PolicyList>
			</Container>
			{origin === InsuranceOrigin.Offer && onShowTermsAndConditions && (
				<ButtonGroup spacing={16} center column>
					{onShowTermsAndConditions && (
						<ButtonTransparent
							label={
								<ButtonLabel>
									<FormattedMessage id='insurance.buttonTermsAndConditions' defaultMessage='Compare all conditions' />
									{areTermsAndConditionsExternal && <IconLink />}
								</ButtonLabel>
							}
							aria-label={`
								${formatMessage({ id: 'insurance.buttonTermsAndConditions', defaultMessage: 'Compare all conditions' })}.
								${areTermsAndConditionsExternal ? `${formatMessage({ id: 'buttonExternal.annotation', defaultMessage: 'You are leaving the app and will be taken to the browser.' })}` : ''}
							`}
							onClick={onShowTermsAndConditions}
						/>
					)}

					{origin === InsuranceOrigin.Offer && (
						<ButtonSolid
							label={
								<ButtonLabel>
									<FormattedMessage id='insurance.buttonContinue' defaultMessage='Purchase insurance' />
									<IconLink />
								</ButtonLabel>
							}
							aria-label={`
								${formatMessage({ id: 'insurance.buttonContinue', defaultMessage: 'Purchase insurance' })}.
								${formatMessage({ id: 'buttonExternal.annotation', defaultMessage: 'You are leaving the app and will be taken to the browser.' })}
							`}
							onClick={onShowSwitching}
						/>
					)}
				</ButtonGroup>
			)}
		</>
	)
}
